<template>
  <div class="edit-page">
    <!-- 页面加载状态显示组件 - 仅在非组件模式下显示 -->
    <div v-if="loading && !isComponent" class="loading-container">
      <el-card class="loading-card">
        <div class="loading-content">
          <i class="el-icon-loading"></i>
          <p>正在加载，请稍等...</p>
        </div>
      </el-card>
    </div>

    <!-- 主要内容区域容器 -->
    <el-container v-else class="container">
      <el-main>
        <!-- 页面头部：标题和保存按钮 -->
        <el-row type="flex" justify="start" align="middle" class="page-header" style="z-index: 2;">
          <el-col :span="11">
            <el-row type="flex" justify="start" align="middle">
              <el-col>
                <h2 class="page-title">{{ filename ? filename.split('-').slice(0,1).join('-') : '新建日志' }}</h2>
                <h2 class="page-title">{{ filename ? filename.split('-').slice(1,2).join('-') : '' }}</h2>
              </el-col>
            </el-row>
          </el-col>
          <el-col :span="13" style="text-align: right">
            <el-button 
              type="primary" 
              icon="el-icon-edit-outline"
              size="medium" 
              @click="saveLog">
              保存
            </el-button>
            <el-button 
              type="default" 
              icon="el-icon-arrow-left"
              size="medium" 
              @click="goBack"
              >
              返回
            </el-button>
          </el-col>
        </el-row>

        <!-- 主要表单区域 -->
        <el-form 
          ref="form" 
          :model="formData" 
          label-position="top"
          class="edit-form"
          v-loading="loading && isComponent"
          element-loading-text="加载中..."
        >
          <!-- 新建日志时显示不可更改的日期 -->
          <el-form-item  label="日期">
            <el-input 
              v-model="formattedDate" 
              disabled
              style="width: 100%"
            ></el-input>
          </el-form-item>
          
          <!-- 新建日志时的日志类型选择器 - 仅显示传递进来的类型 -->
          <el-form-item v-if="!isEdit" label="日志类型">
            <el-input 
              v-if="selectedType" 
              v-model="selectedType" 
              disabled
              style="width: 100%"
            ></el-input>
            <el-select 
              v-else
              v-model="currentType" 
              @change="changeLogType"
              style="width: 100%"
            >
              <el-option
                v-for="type in logTypes"
                :key="type"
                :label="type"
                :value="type"
              >
              </el-option>
            </el-select>
          </el-form-item>
          
          <!-- 根据模板动态生成的表单字段 -->
          <template v-if="template">
            <el-form-item 
              v-for="field in template.fields"
              v-if="field.type !== 'image'"
              :key="field.name"
              :label="field.label"
              :prop="field.name"
              :rules="[{ required: field.required !== false, message: '请填写' + field.label }]"
            >
              <!-- 文本输入框 -->
              <el-input 
                v-if="field.type === 'text'"
                v-model="formData[field.name]"
                :placeholder="'请输入' + field.label"
              >
              </el-input>
              
              <!-- 多行文本框 -->
              <el-input 
                v-if="field.type === 'textarea'"
                type="textarea"
                v-model="formData[field.name]"
                :rows="field.rows || 20"
                :placeholder="'请输入' + field.label"
              >
              </el-input>
              
              
            </el-form-item>
          </template>
          
          <!-- 图片上传和预览区域 -->
          <el-form-item label="照片">
            <el-upload
              action="#"
              :auto-upload="false"
              :on-change="handleFileChange"
              :file-list="newFiles"
              multiple
              accept="image/*"
              class="upload-area"
              ref="upload"
            >
              <el-button size="small" type="primary">选择照片</el-button>
            </el-upload>
            
            <!-- 新上传图片的预览 -->
            <div class="preview-container">
              <div 
                v-for="(file, index) in newFiles" 
                :key="file.uid"
                class="preview-item"
              >
                <img 
                  :src="file.url" 
                  @click="showLocalFullImage(file.url)"
                  class="preview-image"
                >
                <div class="delete-icon" @click="removePreview(index)">
                  <i class="el-icon-close"></i>
                </div>
                <el-button 
                  type="primary" 
                  size="mini"
                  class="copy-button"
                  @click="copyPhotoInsertCode(file.name)"
                >
                  复制图片名称
                </el-button>
              </div>
            </div>
            
            <!-- 已有图片预览 -->
            <div class="thumbnail-container">
              <div 
                v-for="photo in attachments" 
                :key="photo"
                :class="['thumbnail-item', {'to-delete': isPhotoToDelete(photo)}]"
              >
                <img 
                  :src="getPhotoUrl(photo)" 
                  @click="showFullImage(photo)"
                  class="thumbnail"
                  :alt="photo"
                >
                <div 
                  :class="['delete-icon', {'cancel-icon': isPhotoToDelete(photo)}]" 
                  @click="toggleDeletePhoto(photo)"
                >
                  <i :class="isPhotoToDelete(photo) ? 'el-icon-refresh-left' : 'el-icon-close'"></i>
                </div>
                <el-button 
                  type="primary" 
                  size="mini"
                  class="copy-button"
                  @click="copyPhotoInsertCode(photo)"
                >
                  复制图片插入代码
                </el-button>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </el-main>
      
      <!-- 图片全屏预览对话框 - 新样式 -->
      <div class="full-image-viewer" v-if="showFullImageDialog" @click="closeFullImage">
        <div class="full-image-container">
          <img 
            :src="fullImageUrl" 
            class="full-image"
            :class="{'image-loaded': imageLoaded}"
            @load="handleImageLoad"
          >
          <div class="loading-indicator" v-if="!imageLoaded">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
          <a 
            class="download-full-image"
            :href="fullImageUrl"
            :download="currentFullImage"
            @click.stop
          >
            下载原图
          </a>
        </div>
      </div>
    </el-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Editor',
  props: {
    // 添加props，使组件可以接收外部传入的文件名
    propFilename: {
      type: String,
      default: ''
    },
    // 是否作为组件使用
    isComponent: {
      type: Boolean,
      default: false
    }
  },
  // 组件数据定义
  data() {
    return {
      loading: true, // 页面加载状态
      filename: this.isComponent ? this.propFilename : (this.$route && this.$route.query.filename) || '', // 当前编辑的文件名
      logTypes: [], // 可选的日志类型列表
      currentType: '', // 当前选择的日志类型
      template: null, // 当前日志类型的表单模板
      formData: {}, // 表单数据对象
      initialFormData: {}, // 初始表单数据，用于比较是否有修改
      attachments: [], // 已有的附件列表
      photosToDelete: [], // 待删除的照片集合
      newFiles: [], // 新上传的文件列表
      showFullImageDialog: false, // 控制全屏图片预览对话框的显示
      fullImageUrl: '', // 全屏预览的图片URL
      currentFullImage: '', // 当前预览的图片名称
      formChanged: false, // 表单是否已修改
      imageLoaded: false, // 新增：控制图片加载状态
      fromPath: '', // 记录来源页面路径
      selectedDate: this.isComponent ? '' : (this.$route && this.$route.query.date) || '', // 选择的日期
      selectedType: this.isComponent ? '' : (this.$route && this.$route.query.type) || '', // 选择的日志类型
      phone: this.isComponent ? '' : (this.$route && this.$route.query.phone) || '', // 选择的手机号
      username: this.isComponent ? '' : (this.$route && this.$route.query.username) || '' // 选择的姓名
    }
  },

  // 计算属性
  computed: {
    isEdit() {
      return !!this.filename // 判断是否为编辑模式
    },
    photoDeleteMap() {
      return this.photosToDelete.reduce((acc, photo) => {
        acc[photo] = true
        return acc
      }, {})
    },
    // 格式化日期为 xxxx年x月x日 星期x
    formattedDate() {
      // 如果是编辑模式，从文件名中获取日期
      if (this.isEdit && this.filename) {
        try {
          // 假设文件名格式为 YYYYMMDD-类型-其他信息
          const dateStr = this.filename.split('-')[0];
          if (dateStr && dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1; // 月份从0开始
            const day = parseInt(dateStr.substring(6, 8));
            const date = new Date(year, month, day);
            
            if (!isNaN(date.getTime())) {
              const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
              const weekDay = weekDays[date.getDay()];
              return `${year}年${month+1}月${day}日 星期${weekDay}`;
            }
          }
        } catch (e) {
          console.error('从文件名解析日期错误:', e);
        }
      }
      
      // 如果不是编辑模式或从文件名解析失败，则使用selectedDate
      if (!this.selectedDate) return '';
      
      try {
        // 检查日期格式
        let date;
        
        if (this.selectedDate.includes('-')) {
          // 如果是YYYY-MM-DD格式，直接解析
          const parts = this.selectedDate.split('-');
          if (parts.length === 3) {
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1; // 月份从0开始
            const day = parseInt(parts[2]);
            date = new Date(year, month, day);
          } else {
            date = new Date(this.selectedDate);
          }
        } else if (this.selectedDate.length === 8 && /^\d{8}$/.test(this.selectedDate)) {
          // 处理YYYYMMDD格式
          const year = parseInt(this.selectedDate.substring(0, 4));
          const month = parseInt(this.selectedDate.substring(4, 6)) - 1; // 月份从0开始
          const day = parseInt(this.selectedDate.substring(6, 8));
          date = new Date(year, month, day);
        } else {
          // 尝试其他格式解析
          date = new Date(this.selectedDate);
        }
        
        // 检查日期是否有效
        if (isNaN(date.getTime())) {
          console.error('无效的日期格式:', this.selectedDate);
          return this.selectedDate;
        }
        
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        
        // 获取星期几
        const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
        const weekDay = weekDays[date.getDay()];
        
        return `${year}年${month}月${day}日 星期${weekDay}`;
      } catch (e) {
        console.error('日期格式化错误:', e);
        return this.selectedDate;
      }
    },
    // 判断表单是否真的被修改
    isFormActuallyChanged() {
      // 如果有待删除的照片或新上传的文件，则表单已修改
      if (this.photosToDelete.length > 0 || this.newFiles.length > 0) {
        return true;
      }
      
      // 比较当前表单数据和初始表单数据
      const currentFormDataStr = JSON.stringify(this.formData);
      const initialFormDataStr = JSON.stringify(this.initialFormData);
      
      return currentFormDataStr !== initialFormDataStr;
    }
  },

  // 生命周期钩子
  created() {
    // 记录来源页面路径
    this.fromPath = this.isComponent ? '/admin' : (this.$route && this.$route.query.from) || '/files'
    this.loadData() // 加载页面数据
    if (!this.isComponent) {
      window.addEventListener('beforeunload', this.handleBeforeUnload) // 添加页面关闭提醒
    }
  },

  destroyed() {
    if (!this.isComponent) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload) // 移除页面关闭事件监听
    }
  },

  // 添加路由导航守卫
  beforeRouteLeave(to, from, next) {
    // 如果是作为组件使用，不需要路由守卫
    if (this.isComponent) {
      next()
      return
    }
    
    // 如果表单真的被修改，则显示确认对话框
    if (this.isFormActuallyChanged) {
      this.$confirm('您有未保存的更改，确定要离开吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 用户确认离开，设置表单未修改状态
        this.formChanged = false;
        next() // 用户确认离开
      }).catch(() => {
        // 用户取消离开
        next(false) // 用户取消离开
      })
    } else {
      next() // 表单未修改，直接离开
    }
  },

  methods: {
    // 添加返回方法
    goBack() {
      // 如果是作为组件使用，发送关闭事件
      if (this.isComponent) {
        this.$emit('close-editor')
        return
      }
      
      // 如果表单真的被修改，显示确认对话框
      if (this.isFormActuallyChanged) {
        this.$confirm('您有未保存的更改，确定要离开吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 用户确认离开，设置表单未修改状态，避免触发beforeRouteLeave守卫
          this.formChanged = false;
          // 将isFormActuallyChanged的结果也设为false，确保不会触发导航守卫
          this.initialFormData = JSON.parse(JSON.stringify(this.formData));
          this.photosToDelete = [];
          this.newFiles = [];
          
          // 通过父组件设置向右滑动效果
          if (this.$parent && this.$parent.setTransition) {
            this.$parent.setTransition('slide-right')
          }
          // 使用replace而不是push，这样不会在历史记录中留下编辑页面的记录
          this.$router.replace(this.fromPath)
        }).catch(() => {
          // 用户取消离开，不执行任何操作
          console.log('用户取消离开')
        })
      } else {
        // 表单未修改，直接离开
        this.$parent.setTransition('slide-right')
        this.$router.replace(this.fromPath)
      }
    },
    
    // 加载页面初始数据
    async loadData() {
      try {
        // 构造请求参数
        const params = {}
        
        // 如果有文件名，则是编辑模式
        if (this.filename) {
          params.filename = this.filename
        } 
        // 如果没有文件名但有日期和类型，则是指定日期和类型的新建模式
        else if (this.selectedDate && this.selectedType) {
          params.date = this.selectedDate
          params.type = this.selectedType
          // 直接设置当前类型为选择的类型
          this.currentType = this.selectedType
        }
        
        const response = await axios.get('/api/log_templates', {
          params: params
        })
        
        if (response.data.status === 'success') {
          this.logTypes = response.data.log_types
          
          // 如果有指定类型，则使用指定的类型
          if (this.selectedType && !this.filename) {
            this.currentType = this.selectedType
          } else if (!this.selectedType) {
            this.currentType = response.data.current_type
          }
          
          this.template = response.data.template
          
          // 先设置formChanged为false，然后再设置formData
          this.formChanged = false
          this.formData = response.data.form_data
          // 保存初始表单数据的深拷贝
          this.initialFormData = JSON.parse(JSON.stringify(response.data.form_data || {}))
          this.attachments = response.data.attachments
          
          // 确保在数据加载完成后，表单状态为未修改
          this.$nextTick(() => {
            this.formChanged = false
          })
        } else {
          this.$message.error(response.data.error || '加载数据失败')
        }
      } catch (error) {
        this.$message.error(error.response?.data?.error || '加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 保存成功的处理函数
    handleSave() {
      this.$message({
        message: '保存成功',
        type: 'success'
      })
    },

    // 切换日志类型的处理函数
    async changeLogType(type) {
      this.loading = true
      try {
        const response = await axios.get('/api/log_templates', {
          params: { type }
        })
        if (response.data.status === 'success') {
          this.currentType = type
          this.template = response.data.template
          
          // 先设置formChanged为false，然后再设置formData
          this.formChanged = false
          this.formData = {}
          // 保存初始表单数据的深拷贝
          this.initialFormData = {}
          
          // 确保在数据加载完成后，表单状态为未修改
          this.$nextTick(() => {
            this.formChanged = false
          })
        }
      } catch (error) {
        this.$message.error('切换日志类型失败')
      } finally {
        this.loading = false
      }
    },

    // 保存日志的主要方法
    async saveLog() {
      let loading = null;
      try {
        // 表单验证
        await this.$refs.form.validate()
        
        // 构建表单数据
        const formData = new FormData()
        
        // 添加基本信息
        formData.append('log_type', this.currentType)
        
        // 添加表单字段
        for (const key in this.formData) {
          formData.append(key, this.formData[key])
        }
        
        // 添加格式化的日期字符串到表单数据
        formData.append('formatted_date', this.formattedDate)
        
        // 添加文件名（如果是编辑模式）
        if (this.filename) {
          formData.append('filename', this.filename)
        } else {
          // 如果文件名为空，则构建一个文件名
          let dateStr = ''
          
          // 优先从selectedDate获取日期
          if (this.selectedDate) {
            // 处理不同格式的日期
            if (this.selectedDate.includes('-')) {
              // 如果是YYYY-MM-DD格式，转换为YYYYMMDD
              dateStr = this.selectedDate.replace(/-/g, '')
            } else if (this.selectedDate.length === 8 && /^\d{8}$/.test(this.selectedDate)) {
              // 如果已经是YYYYMMDD格式，直接使用
              dateStr = this.selectedDate
            } else {
              // 尝试解析其他格式
              try {
                const date = new Date(this.selectedDate)
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear()
                  const month = (date.getMonth() + 1).toString().padStart(2, '0')
                  const day = date.getDate().toString().padStart(2, '0')
                  dateStr = `${year}${month}${day}`
                }
              } catch (e) {
                console.error('日期解析错误:', e)
              }
            }
          }
          
          // 如果从selectedDate获取失败，则从formattedDate提取
          if (!dateStr) {
            const dateMatch = this.formattedDate.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
            if (dateMatch) {
              const year = dateMatch[1]
              const month = dateMatch[2].padStart(2, '0')
              const day = dateMatch[3].padStart(2, '0')
              dateStr = `${year}${month}${day}`
            }
          }
          
          // 构建文件名格式：日期-日志类型-姓名-手机号
          // 注意：姓名和手机号需要从表单数据中获取，如果表单中没有这些字段，则使用默认值或空值

          const newFilename = `${dateStr}-${this.currentType}-${this.username}-${this.phone}`
          formData.append('filename', newFilename)
        }
        
        // 添加待删除的照片
        this.photosToDelete.forEach(photo => {
          formData.append('photos_to_delete[]', photo)
        })
        
        // 添加新上传的文件
        this.newFiles.forEach(file => {
          formData.append('files[]', file.raw)
          // 添加自定义文件名
          if (file.customName) {
            formData.append('file_names[]', file.customName)
          }
        })
        
        // 显示加载状态
       loading = this.$loading({
          lock: true,
          text: '保存中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 发送请求
        const response = await fetch('/api/save_log', {
          method: 'POST',
          body: formData
        })
        
        // 检查响应状态
        if (!response.ok) {
          throw new Error('保存失败')
        }
        
        // 解析响应数据
        const data = await response.json()
        
        // 关闭加载状态
        loading.close()
        
        // 处理响应
        if (data.status === 'success') {
          // 显示成功消息
          this.$message.success('保存成功')
          
          // 更新表单状态
          this.formChanged = false
          this.initialFormData = JSON.parse(JSON.stringify(this.formData))
          this.photosToDelete = []
          this.newFiles = []
          
          // 如果是新建模式，更新文件名
          if (!this.isEdit && data.filename) {
            this.filename = data.filename
          }
          
          // 如果是作为组件使用，发送保存成功事件
          if (this.isComponent) {
            this.$emit('save-success')
          } else {
            // 如果不是从管理页面来的，则返回文件列表页面
            if (this.fromPath !== '/admin') {
              this.$router.replace('/files')
            } else {
              // 否则返回管理页面
              this.$router.replace('/admin')
            }
          }
        } else {
          // 显示错误消息
          this.$message.error(data.message || '保存失败')
          loading.close()
        }
      } catch (error) {
        console.error('保存日志失败:', error)
        this.$message.error('保存失败: ' + error.message)
        loading.close()
      }
    },

    // 修改文件改变处理函数
    handleFileChange(file, fileList) {
      this.formChanged = true
      
      // 为新文件生成名称
      if (!file.customName && file.raw) {
        // 获取文件扩展名
        const ext = file.name.split('.').pop()
        // 生成新的文件名
        const newName = this.generateImageName(ext)
        // 保存原始文件名和新文件名
        file.originalName = file.name
        file.customName = newName
        file.name = newName
      }
      
      // 更新文件列表
      this.newFiles = fileList
      
      // 创建本地预览URL
      if (!file.url && file.raw) {
        // 读取文件并创建预览
        const reader = new FileReader()
        reader.onload = (e) => {
          file.url = e.target.result
          // 强制更新视图
          this.$forceUpdate()
        }
        reader.readAsDataURL(file.raw)
      }
    },

    // 生成图片名称的方法
    generateImageName(extension) {
      // 查找当前最大的图片编号
      let maxNumber = 0
      const regex = /^图片(\d+)\.\w+$/
      
      // 检查现有附件中的编号
      this.attachments.forEach(photo => {
        if (!this.photosToDelete.includes(photo)) {
          const match = photo.match(regex)
          if (match && match[1]) {
            const num = parseInt(match[1])
            if (num > maxNumber) {
              maxNumber = num
            }
          }
        }
      })
      
      // 检查新上传文件中的编号
      this.newFiles.forEach(file => {
        if (file.customName) {
          const match = file.customName.match(regex)
          if (match && match[1]) {
            const num = parseInt(match[1])
            if (num > maxNumber) {
              maxNumber = num
            }
          }
        }
      })
      
      // 生成新的图片名称并检查是否重复
      let newNumber = maxNumber + 1
      let newName = `图片${newNumber}.${extension}`
      
      // 检查生成的名称是否已存在，如果存在则递增编号
      while (this.isFileNameExists(newName)) {
        newNumber++
        newName = `图片${newNumber}.${extension}`
      }
      
      return newName
    },
    
    // 检查文件名是否已存在
    isFileNameExists(fileName) {
      // 检查现有附件中是否存在该文件名
      const existingAttachments = this.attachments.filter(photo => !this.photosToDelete.includes(photo))
      if (existingAttachments.includes(fileName)) {
        return true
      }
      
      // 检查新上传文件中是否存在该文件名
      for (const file of this.newFiles) {
        if (file.customName === fileName || file.name === fileName) {
          return true
        }
      }
      
      return false
    },

    // 修改移除预览图片的方法
    removePreview(index) {
      const files = [...this.newFiles]
      files.splice(index, 1)
      this.newFiles = files
      this.formChanged = true
      
      // 同步更新上传组件的文件列表
      if (this.$refs.upload) {
        this.$refs.upload.handleRemove(files[index])
      }
    },

    // 修改切换照片删除状态的方法
    toggleDeletePhoto(photo) {
      const index = this.photosToDelete.indexOf(photo)
      if (index > -1) {
        this.photosToDelete.splice(index, 1)
      } else {
        this.photosToDelete.push(photo)
      }
      this.formChanged = true
    },

    // 修改检查照片是否标记为删除的方法
    isPhotoToDelete(photo) {
      return this.photoDeleteMap[photo] || false
    },

    // 获取照片缩略图URL
    getPhotoUrl(photo) {
      if (!this.filename) return ''
      const yearMonth = this.filename.substring(0, 6)
      return `/api/get_photos/${yearMonth}/${this.filename}/${photo}?thumb=true`
    },

    // 显示本地图片全屏预览
    showLocalFullImage(url) {
      this.imageLoaded = false
      this.fullImageUrl = url
      this.showFullImageDialog = true
    },

    // 显示已有图片全屏预览
    showFullImage(photo) {
      this.imageLoaded = false
      this.currentFullImage = photo
      this.fullImageUrl = `/api/get_photos/${this.filename.substring(0, 6)}/${this.filename}/${photo}?thumb=false`
      this.showFullImageDialog = true
    },

    // 关闭全屏预览
    closeFullImage() {
      this.showFullImageDialog = false
      this.fullImageUrl = ''
      this.currentFullImage = ''
      this.imageLoaded = false
    },

    // 处理图片加载完成
    handleImageLoad() {
      this.imageLoaded = true
    },

    // 页面关闭前的确认处理
    handleBeforeUnload(e) {
      if (this.isFormActuallyChanged) {
        console.log('handleBeforeUnload')
        e.preventDefault()
        e.returnValue = '您有未保存的更改，确定要离开吗？'
      }
    },

    // 复制图片名称
    copyPhotoInsertCode(name) {
      const input = document.createElement('input')
      input.value = '[在此插入' + name + ']'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('图片插入代码已复制到剪贴板')
    },

    // 获取已选择图片的URL
    getSelectedImageUrl(fieldName) {
      const selectedValue = this.formData[fieldName]
      if (!selectedValue) return ''
      
      // 检查是否是新上传的图片
      const newFile = this.newFiles.find(file => file.name === selectedValue)
      if (newFile && newFile.url) {
        return newFile.url
      }
      
      // 否则是已有的图片
      return this.getPhotoUrl(selectedValue)
    }
  },

  // 监听器
  watch: {
    // 监听表单数据变化
    formData: {
      handler(newVal, oldVal) {
        // 只有当表单真正被修改时才标记为已修改
        if (oldVal && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.formChanged = true; // 标记表单已修改
        }
      },
      deep: true // 深度监听对象变化
    }
  }
}
</script>

<style scoped>
.el-main {
  padding: 0px;
}
/* 页面整体样式 */
.edit-page {
  min-height: 100vh;
  background-color: #f5f7fa;
}

/* 主容器样式 */
.container {
  max-width: 1340px;
  min-width: 300px;
  margin: 0 auto;
  padding: 20px;
}

/* 页面头部样式 */
.page-header {
  margin-bottom: 0px;
  background: white;
  padding: 15px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.page-title {
  font-size: 20px;
  color: #303133;
  margin: 0;
  font-weight: 500;
}

/* 加载状态样式 */
.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-card {
  width: 300px;
  text-align: center;
}

.loading-content {
  padding: 20px;
}

.loading-content i {
  font-size: 30px;
  margin-bottom: 10px;
  color: #409EFF;
}

.loading-content p {
  margin: 10px 0 0;
  color: #606266;
}

/* 表单样式 */
.edit-form {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

/* 照片预览区域样式 */
.photo-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.photo-item {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: 4px;
  overflow: hidden;
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-item.to-delete img {
  opacity: 0.5;
}

.delete-button {
  position: absolute;
  bottom: 0px;
  right: 0px;
  padding: 4px 8px;
  background-color: rgba(255, 59, 48, 0.9);
  color: white;
  border: none;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.delete-button:hover {
  background-color: rgba(255, 59, 48, 1);
}

.full-image-dialog {
  :deep(.el-dialog) {
    margin: 0 !important;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 90%;
    max-height: 90%;
  }

  :deep(.el-dialog__header),
  :deep(.el-dialog__footer) {
    display: none;
  }

  :deep(.el-dialog__body) {
    padding: 0;
    background-color: transparent;
  }
}

.full-image-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  min-width: 200px;
  min-height: 200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.full-image {
  max-width: 100%;
  max-height: calc(90vh - 50px);
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.3s;
}

.full-image.loaded {
  opacity: 1;
}

.download-full-image {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 14px;
  cursor: pointer;
  background-color: #4a90e2;
  border: none;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
  text-align: center;
  width: auto;
  min-width: 200px;
  z-index: 2001;
}

.download-full-image:hover {
  background-color: #357abd;
  text-decoration: none;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a90e2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@keyframes spin {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

.upload-area {
  :deep(.el-upload-list) {
    display: none;
  }
}

/* 响应式布局样式 */
@media screen and (max-width: 768px) {
  .container {
    padding: 10px;
  }
  
  .header {
    margin-bottom: 15px;
  }
  
  .edit-form {
    padding: 15px;
  }
}

/* 修改预览和缩略图样式 */
.preview-container,
.thumbnail-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;
}

.preview-item,
.thumbnail-item {
  position: relative;
  width: 140px;
  height: 170px; /* 增加高度以容纳底部的复制按钮 */
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s; /* 添加过渡效果 */
  display: flex;
  flex-direction: column;
}

.preview-image,
.thumbnail {
  width: 100%;
  height: 140px; /* 固定图片高度 */
  object-fit: cover;
  transition: transform 0.2s, opacity 0.2s; /* 添加透明度过渡 */
}

.preview-image:hover,
.thumbnail:hover {
  transform: scale(1.05);
}

/* 修改待删除状态的样式 - 调整顺序和结构 */
.thumbnail-item.to-delete {
  border: 2px solid #ff4444;
  box-shadow: 0 0 0 1px #ff4444;
}

.thumbnail-item.to-delete .thumbnail {
  opacity: 0.5;
}

/* 删除图标样式 */
.delete-icon {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background-color: rgba(255, 59, 48, 0.9);
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 10;
}

.delete-icon:hover {
  background-color: rgba(255, 59, 48, 1);
}

.delete-icon i {
  font-size: 14px;
}

/* 取消图标样式 */
.cancel-icon {
  background-color: rgba(64, 158, 255, 0.9);
}

.cancel-icon:hover {
  background-color: rgba(64, 158, 255, 1);
}

/* 复制按钮样式 */
.copy-button {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  border-radius: 0;
  margin: 0;
  padding: 10px 0;
  font-size: 12px;
  text-align: center;
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  transition: background-color 0.2s;
}

.copy-button:hover {
  background-color: rgba(64, 158, 255, 1);
}

/* 图片全屏预览对话框 - 新样式 */
.full-image-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.full-image-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  min-width: 200px;
  min-height: 200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.full-image {
  max-width: 100%;
  max-height: calc(90vh - 50px);
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.3s;
}

.full-image.image-loaded {
  opacity: 1;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  text-align: center;
}

.loading-indicator i {
  margin-bottom: 10px;
}

.download-full-image {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 14px;
  cursor: pointer;
  background-color: #4a90e2;
  border: none;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
  text-align: center;
  width: auto;
  min-width: 200px;
  z-index: 2001;
}

.download-full-image:hover {
  background-color: #357abd;
  text-decoration: none;
}
</style> 